import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Key, 
  ShoppingCart, 
  Copy, 
  Check, 
  X, 
  Users, 
  RefreshCw,
  Search,
  Ban,
  Calendar,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Plus
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const ActivationCodesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedCode, setCopiedCode] = useState(null);
  const [expandedOrders, setExpandedOrders] = useState({}); // Track which orders are expanded

  // Manual code generation form
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualForm, setManualForm] = useState({
    order_number: "",
    customer_id: "",
    customer_email: "",
    plan: "demo",
    license_count: 1
  });
  const [manualLoading, setManualLoading] = useState(false);

  const handleManualGenerate = async (e) => {
    e.preventDefault();
    if (!manualForm.order_number.trim() || !manualForm.customer_id.trim()) {
      toast.error("Numéro de commande et ID client requis");
      return;
    }
    setManualLoading(true);
    try {
      const res = await axios.post(`${API}/admin/activation-codes/manual-generate`, {
        order_number: manualForm.order_number.trim(),
        customer_id: manualForm.customer_id.trim(),
        customer_email: manualForm.customer_email.trim(),
        plan: manualForm.plan,
        license_count: parseInt(manualForm.license_count, 10) || 1
      });
      const codes = res.data?.activation_codes || [];
      toast.success(`${codes.length} code(s) créé(s) pour commande #${manualForm.order_number}`);
      setManualForm({ order_number: "", customer_id: "", customer_email: "", plan: "demo", license_count: 1 });
      setShowManualForm(false);
      fetchCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la génération");
    } finally {
      setManualLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/search');
      return;
    }
    fetchCodes();
  }, [user, navigate]);

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/activation-codes`);
      setOrders(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement des codes:", error);
      toast.error("Erreur lors du chargement des codes");
    } finally {
      setLoading(false);
    }
  };

  const toggleOrder = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success("Code copié!");
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const revokeLicense = async (licenseId) => {
    if (!confirm(`Êtes-vous sûr de vouloir révoquer cette licence?`)) {
      return;
    }
    
    try {
      await axios.post(`${API}/admin/activation-codes/${licenseId}/revoke`);
      toast.success("Licence révoquée");
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la révocation");
    }
  };

  const filteredOrders = orders.filter(order => 
    (order.buyer_email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (order.order_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (order.order_number?.toString() || '').includes(searchTerm)
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'unused':
        return (
          <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1">
            <Check className="w-3 h-3" />
            Disponible
          </span>
        );
      case 'active':
        return (
          <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full flex items-center gap-1">
            <Users className="w-3 h-3" />
            Activé
          </span>
        );
      case 'revoked':
        return (
          <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full flex items-center gap-1">
            <X className="w-3 h-3" />
            Révoqué
          </span>
        );
      default:
        return null;
    }
  };

  const getPlanBadge = (plan) => {
    const planConfig = {
      'demo': { label: 'Démo', color: 'text-yellow-400 bg-yellow-500/20' },
      'monthly': { label: 'Mensuel', color: 'text-blue-400 bg-blue-500/20' },
      'annual': { label: 'Annuel', color: 'text-green-400 bg-green-500/20' }
    };
    const config = planConfig[plan] || { label: plan, color: 'text-gray-400 bg-gray-500/20' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto fade-in">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate("/search")}
              variant="outline"
              className="bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Key className="w-8 h-8 text-amber-500" />
                Codes d'activation
              </h1>
              <p className="text-gray-400 mt-1">Gestion des licences par commande</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setShowManualForm(v => !v)}
              variant="outline"
              className="bg-amber-600/10 border-amber-700 text-amber-300 hover:bg-amber-600/20"
              data-testid="toggle-manual-generate-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {showManualForm ? "Fermer" : "Générer manuellement"}
            </Button>
            <Button
              onClick={fetchCodes}
              variant="outline"
              className="bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
        </div>

        {/* Manual generation form */}
        {showManualForm && (
          <div className="mb-6 glass rounded-xl border border-amber-800/50 bg-amber-900/10 p-5" data-testid="manual-generate-panel">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
              <div>
                <h3 className="text-white font-semibold">Générer des codes pour une commande sans webhook</h3>
                <p className="text-gray-400 text-sm mt-1">
                  À utiliser quand le webhook Shopify n'a pas créé les codes (ex: commandes démo $0).
                  Format généré : <code className="text-amber-400">{"{commande}-{client}-{type}-{n°}"}</code>
                </p>
              </div>
            </div>
            <form onSubmit={handleManualGenerate} className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div className="md:col-span-1">
                <label className="text-xs text-gray-400 mb-1 block">N° de commande *</label>
                <Input
                  type="text"
                  placeholder="1045"
                  value={manualForm.order_number}
                  onChange={(e) => setManualForm({...manualForm, order_number: e.target.value})}
                  className="bg-gray-900/50 border-gray-700 text-white"
                  data-testid="manual-order-number-input"
                />
              </div>
              <div className="md:col-span-1">
                <label className="text-xs text-gray-400 mb-1 block">ID client Shopify *</label>
                <Input
                  type="text"
                  placeholder="8725655060641"
                  value={manualForm.customer_id}
                  onChange={(e) => setManualForm({...manualForm, customer_id: e.target.value})}
                  className="bg-gray-900/50 border-gray-700 text-white"
                  data-testid="manual-customer-id-input"
                />
              </div>
              <div className="md:col-span-1">
                <label className="text-xs text-gray-400 mb-1 block">Email (optionnel)</label>
                <Input
                  type="email"
                  placeholder="client@ex.com"
                  value={manualForm.customer_email}
                  onChange={(e) => setManualForm({...manualForm, customer_email: e.target.value})}
                  className="bg-gray-900/50 border-gray-700 text-white"
                  data-testid="manual-customer-email-input"
                />
              </div>
              <div className="md:col-span-1">
                <label className="text-xs text-gray-400 mb-1 block">Plan *</label>
                <select
                  value={manualForm.plan}
                  onChange={(e) => setManualForm({...manualForm, plan: e.target.value})}
                  className="w-full h-10 rounded-md bg-gray-900/50 border border-gray-700 text-white px-3 text-sm"
                  data-testid="manual-plan-select"
                >
                  <option value="demo">Démo (3 jours)</option>
                  <option value="monthly">Mensuel</option>
                  <option value="annual">Annuel</option>
                </select>
              </div>
              <div className="md:col-span-1">
                <label className="text-xs text-gray-400 mb-1 block">Nb licences *</label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={manualForm.license_count}
                  onChange={(e) => setManualForm({...manualForm, license_count: e.target.value})}
                  className="bg-gray-900/50 border-gray-700 text-white"
                  data-testid="manual-license-count-input"
                />
              </div>
              <div className="md:col-span-5 flex justify-end gap-2 mt-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowManualForm(false)}
                  className="bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={manualLoading}
                  className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
                  data-testid="manual-generate-submit-btn"
                >
                  {manualLoading ? "Génération..." : "Générer les codes"}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
            <Input
              type="text"
              placeholder="Rechercher par email, code ou numéro de commande..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-gray-900/50 border-gray-700 text-white"
            />
          </div>
        </div>

        {/* Orders List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-amber-500" />
            <p className="text-gray-400 mt-4">Chargement...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-12 glass rounded-xl border border-gray-800">
            <Key className="w-12 h-12 mx-auto text-gray-600" />
            <p className="text-gray-400 mt-4">
              {searchTerm ? "Aucune commande trouvée" : "Aucune licence enregistrée"}
            </p>
            <p className="text-gray-500 text-sm mt-2">
              Les licences sont créées automatiquement lors des achats sur Shopify
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredOrders.map((order, index) => {
              // Check if all licenses are revoked
              const allRevoked = order.revoked === order.total_codes;
              const isExpanded = expandedOrders[order.order_id];
              
              return (
              <div key={index} className={`glass rounded-xl border overflow-hidden ${
                allRevoked ? 'border-red-800/50 bg-red-900/10' : 'border-gray-800'
              }`}>
                {/* Order Header - Clickable */}
                <div 
                  className={`p-4 cursor-pointer hover:bg-gray-800/30 transition-colors ${
                    allRevoked ? 'bg-red-900/20' : 'bg-gray-900/50'
                  }`}
                  onClick={() => toggleOrder(order.order_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Expand/Collapse Icon */}
                      <div className="text-gray-400">
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5" />
                        ) : (
                          <ChevronRight className="w-5 h-5" />
                        )}
                      </div>
                      <ShoppingCart className={`w-8 h-8 p-1.5 rounded-lg ${
                        allRevoked ? 'text-red-500 bg-red-500/20' : 'text-amber-500 bg-amber-500/20'
                      }`} />
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-white">Commande #{order.order_number}</p>
                          {getPlanBadge(order.plan)}
                          {allRevoked && (
                            <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              Annulée
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-400">{order.buyer_email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <Calendar className="w-4 h-4" />
                          Expire: {formatDate(order.expires_at)}
                        </div>
                        <p className="text-sm mt-1">
                          {order.available > 0 && <span className="text-green-400">{order.available} dispo</span>}
                          {order.available > 0 && (order.used > 0 || order.revoked > 0) && ' / '}
                          {order.used > 0 && <span className="text-blue-400">{order.used} activé</span>}
                          {order.used > 0 && order.revoked > 0 && ' / '}
                          {order.revoked > 0 && <span className="text-red-400">{order.revoked} révoqué</span>}
                          <span className="text-gray-500"> ({order.total_codes} total)</span>
                        </p>
                      </div>
                      {!allRevoked && (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyCode(order.order_id);
                          }}
                          variant="outline"
                          size="sm"
                          className="border-amber-600 text-amber-400 hover:bg-amber-600/10"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copier
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Content - Licenses Table */}
                {isExpanded && (
                  <div className="border-t border-gray-800">
                    {/* Activation Code Display */}
                    {!allRevoked && (
                      <div className="p-4 bg-amber-900/10 border-b border-gray-800">
                        <p className="text-xs text-amber-400 mb-1">Code d'activation:</p>
                        <code className="font-mono text-lg text-amber-300">{order.order_id}</code>
                      </div>
                    )}
                    
                    {/* Licenses Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-900/30">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Licence</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Statut</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Activé par</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Date</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800">
                          {order.codes.map((license, licIndex) => (
                            <tr key={licIndex} className={`hover:bg-gray-800/30 ${license.status === 'revoked' ? 'bg-red-900/10' : ''}`}>
                              <td className="px-4 py-3">
                                <span className="text-white font-medium">
                                  #{license.license_number}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                {getStatusBadge(license.status)}
                              </td>
                              <td className="px-4 py-3 text-gray-300">
                                {license.used_by_email || '-'}
                              </td>
                              <td className="px-4 py-3 text-gray-400 text-sm">
                                {formatDate(license.used_at)}
                              </td>
                              <td className="px-4 py-3 text-right">
                                {license.status === 'unused' && (
                                  <Button
                                    onClick={() => revokeLicense(license.id)}
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                  >
                                    <Ban className="w-4 h-4" />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivationCodesPage;
